import { addSeconds, parseISO } from 'date-fns'

import type { VendorCode } from 'utils/vendorDisplayName'

import type { FilterableRecord, NormalizedStatisticsApiRow } from './filterableRecord'

function safeParseDate(s: string | undefined | null): Date | null {
  if (!s) return null
  try {
    const d = parseISO(String(s).replace(' ', 'T'))
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

/**
 * Map one normalized statistics API row → FilterableRecord (facilityIds filled separately).
 */
export function normalizedStatisticsRowToFilterable(row: NormalizedStatisticsApiRow): FilterableRecord | null {
  const robotSn = String(row.robot_sn ?? '').trim()
  if (!robotSn) return null

  const startAt =
    safeParseDate(row.start_time) ||
    safeParseDate(row.created_at)
  if (!startAt) return null

  const vendor = ((row.vendor as VendorCode) || 'AUTONOMICS') as VendorCode

  let mapId: string | null = null
  let scenarioId: number | null = null
  const som = row.scenario_or_map_id
  if (typeof som === 'string' && som.trim()) {
    mapId = som.trim()
  } else if (typeof som === 'number' && Number.isFinite(som)) {
    scenarioId = som
  }

  const explicitScenario = row.scenario_id
  if (typeof explicitScenario === 'number' && Number.isFinite(explicitScenario)) {
    scenarioId = explicitScenario
  }

  let missionId: number | null = null
  const moa = row.mission_or_area_id
  if (typeof moa === 'number' && Number.isFinite(moa)) {
    missionId = moa
  } else if (typeof moa === 'string' && moa.trim() && /^\d+$/.test(moa.trim())) {
    missionId = Number(moa.trim())
  }

  const missionsInfoIds: number[] = []
  const missionsInfo: Array<{ id: number; name?: string }> = []
  for (const m of row.missions_info ?? []) {
    const id = m?.id
    let num: number | null = null
    if (typeof id === 'number' && Number.isFinite(id)) num = id
    else if (typeof id === 'string' && /^\d+$/.test(id.trim())) num = Number(id.trim())
    if (num != null) {
      missionsInfoIds.push(num)
      missionsInfo.push({ id: num, name: m?.name })
    }
  }

  let endAt = safeParseDate(row.end_time)
  const dur = row.duration_actual
  if (!endAt && typeof dur === 'number' && dur > 0) {
    endAt = addSeconds(startAt, dur)
  }

  const origin = row.vendor_origin_record_id
  const taskId = origin === undefined || origin === null ? null : origin
  const primaryName = String(row.name ?? '').trim()
  const displayName = primaryName || row.event_id

  return {
    vendor,
    robotSn,
    robotId: typeof row.robot_id === 'number' ? row.robot_id : null,
    facilityIds: [],
    missionId,
    scenarioId,
    mapId,
    missionsInfoIds,
    missionsInfo: missionsInfo.length ? missionsInfo : undefined,
    taskId,
    displayName,
    startAt,
    endAt,
    sourceEventId: String(row.event_id ?? ''),
    vendorOriginRecordType: row.vendor_origin_record_type ?? null,
    missionsProgress: Array.isArray(row.missions_progress) ? row.missions_progress : [],
    overallProgress: {
      name: displayName || undefined,
      duration_actual: row.duration_actual ?? undefined,
      duration_overall: row.duration_overall ?? row.duration_actual ?? undefined,
      cleaned_area_actual: row.cleaned_area_actual ?? undefined,
      cleaned_area_planned: row.cleaned_area_planned ?? undefined,
      cleaning_performance: row.cleaning_performance ?? undefined,
      battery_begin: row.battery_begin ?? undefined,
      battery_end: row.battery_end ?? undefined,
      completion_percentage: row.completion_percentage ?? undefined,
      travel_distance_actual: row.travel_distance_actual ?? undefined,
      travel_distance_planned: row.travel_distance_planned ?? undefined,
      water_consumption: row.water_consumption ?? undefined,
      water_consumption_unit: row.water_consumption_unit ?? undefined,
      dirty_water_consumption: row.dirty_water_consumption ?? undefined,
      dirty_water_consumption_unit: row.dirty_water_consumption_unit ?? undefined,
      garbage_filled_begin: row.garbage_filled_begin ?? undefined,
      garbage_filled_end: row.garbage_filled_end ?? undefined,
      missions: missionId != null ? [missionId] : missionsInfoIds,
    },
    executionStatus: typeof row.execution_status === 'number' ? row.execution_status : null,
  }
}

export function enrichFilterableFacilityIds(
  r: FilterableRecord,
  robotIdToFacilityIds: Map<number, number[]>
): FilterableRecord {
  if (!r.robotId) return { ...r, facilityIds: [] }
  return { ...r, facilityIds: robotIdToFacilityIds.get(r.robotId) ?? [] }
}
